import { FirebaseApp } from 'firebase/app';
import {
    AppCheck,
    initializeAppCheck,
    getToken,
    ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import { createLogger } from '../utils/logger';
import { getEnvVars } from '../utils/env';
import { errorHandler } from '../utils/errorHandler';

const logger = createLogger('AppCheck');

let appCheckInstance: AppCheck | null = null;

/**
 * App Check health signal for surfacing attestation outages in the UI.
 *
 * - `unknown`   — App Check has not yet initialized or minted a token, or it
 *                 is intentionally inactive (emulator mode / missing site key).
 * - `healthy`   — App Check successfully minted a token.
 * - `degraded`  — App Check failed to initialize OR a token mint failed. The
 *                 platform falls back to best-effort (token-less) requests; the
 *                 API server's enforcement mode decides whether they're rejected.
 * - `refused`   — App Check REFUSED this browser (HTTP 403: the reCAPTCHA
 *                 Enterprise score was under the project's minimum). FINAL for
 *                 this page: the SDK then blocks every further attempt for 24
 *                 hours, in memory, so nothing but a reload can recover it. See
 *                 {@link handleDefinitiveRefusal}.
 */
export type AppCheckHealth = 'unknown' | 'healthy' | 'degraded' | 'refused';

let appCheckHealth: AppCheckHealth = 'unknown';
const healthSubscribers = new Set<(h: AppCheckHealth) => void>();

const setAppCheckHealth = (next: AppCheckHealth): void => {
    if (appCheckHealth === next) {
        return;
    }
    // `refused` is final for the page; only a real token could supersede it.
    if (appCheckHealth === 'refused' && next !== 'healthy') {
        return;
    }
    appCheckHealth = next;
    healthSubscribers.forEach((cb) => {
        try {
            cb(next);
        } catch {
            // A misbehaving subscriber must never break attestation flow.
        }
    });
};

/**
 * Number of CONSECUTIVE hard token-mint failures required before we surface
 * the degraded "it's us" banner. The banner is an outage signal, not a
 * slow-network signal: a single transient miss on a fresh page load (cold
 * reCAPTCHA challenge, first request in an incognito session) is normal and
 * must not alarm the user. Only a sustained inability to attest — multiple
 * back-to-back hard failures with no success in between — flips to degraded.
 * Any single success resets the counter and clears the banner immediately.
 */
const DEGRADED_FAILURE_THRESHOLD = 3;
let consecutiveTokenFailures = 0;

/**
 * Distinguishes "the mint timed out" (slow, but the service may be fine) from
 * "the mint hard-failed" (an actual error from the SDK). Timeouts must NOT
 * count toward the degraded threshold — a slow-but-working attestation path
 * is the single biggest source of false-positive outage banners.
 */
class AppCheckTokenTimeoutError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'AppCheckTokenTimeoutError';
    }
}

/**
 * A refusal the SDK will not retry: `appCheck/initial-throttle` (the exchange
 * itself answered 403/404) or `appCheck/throttled` (a later ask, blocked by that
 * answer). The SDK's `setBackoff` blocks 403/404 for ONE DAY, in memory
 * (@firebase/app-check `ONE_DAY`), so every ask after the first fails instantly.
 *
 * Measured 2026-09-26 on houseofphoct.com with a refused browser: the ready-gate
 * below asked 190 times in 8 s, every answer the same block; the menu reads then
 * went out token-less, came back permission-denied, and were retried as an App
 * Check "cold start" for another 20-50 s before any error showed. A refused
 * customer stared at "Loading the menu…" for up to a minute, with a Try again
 * button that could not work, because only a reload clears the block.
 */
export function isDefinitiveAppCheckRefusal(error: unknown): boolean {
    const e = error as { code?: unknown; customData?: { httpStatus?: unknown } } | null;
    const code = e?.code;
    if (code !== 'appCheck/initial-throttle' && code !== 'appCheck/throttled') return false;
    const status = Number(e?.customData?.httpStatus);
    return status === 403 || status === 404;
}

/** What a refusal listener is told. */
export interface AppCheckRefusal {
    /**
     * `refused` — App Check refused this page load.
     * `recovered-after-reload` — this page is the recovery reload, and its
     *   fresh check PASSED: the reload rescued a guest who would otherwise
     *   have been stuck. Reported so the recovery rate is a number.
     */
    readonly kind: 'refused' | 'recovered-after-reload';
    /** This page is the reload the recovery triggered. */
    readonly afterReload: boolean;
    /** The recovery is about to reload the page. */
    readonly willReload: boolean;
}

const refusalListeners = new Set<(r: AppCheckRefusal) => void>();
/** Events raised before any listener subscribed (Sentry loads lazily). */
const pendingRefusalEvents: AppCheckRefusal[] = [];
let refusalHandled = false;
let recoveryReported = false;

const emitRefusalEvent = (event: AppCheckRefusal): void => {
    if (refusalListeners.size === 0) {
        pendingRefusalEvents.push(event);
        return;
    }
    refusalListeners.forEach((listener) => {
        try {
            listener(event);
        } catch {
            // Reporting must never block the recovery.
        }
    });
};
let reloadOnceOnRefusal = false;

/** Session key marking "we already reloaded once for a refusal". */
const RELOAD_MARK_KEY = 'orderup.appCheckRefusalReloadAt';
/** A reload older than this no longer counts — a later refusal gets its own. */
const RELOAD_MARK_TTL_MS = 10 * 60 * 1000;

const readReloadMark = (): number | null => {
    try {
        const raw = window.sessionStorage.getItem(RELOAD_MARK_KEY);
        const at = raw ? Number(raw) : NaN;
        return Number.isFinite(at) && Date.now() - at < RELOAD_MARK_TTL_MS ? at : null;
    } catch {
        return null;
    }
};

/**
 * Customer-facing apps opt in: on a definitive refusal, reload the page ONCE.
 * The block lives in the SDK's memory, so a reload is the only thing that
 * clears it, and a fresh reCAPTCHA run usually scores differently — which is
 * exactly what customers were doing by hand ("refresh fixes it"). Guarded by a
 * session mark so it can never loop; staff apps do not opt in.
 */
export const enableAppCheckRefusalRecovery = (): void => {
    reloadOnceOnRefusal = true;
};

/**
 * Be told when App Check refuses this browser. Fires at most once per page.
 * Used to report the refusal (Sentry) — the page states read
 * {@link getAppCheckHealth} / `useAppCheckHealth` instead.
 */
export const onAppCheckRefused = (listener: (r: AppCheckRefusal) => void): (() => void) => {
    refusalListeners.add(listener);
    // Deliver anything raised before the reporter finished loading.
    while (pendingRefusalEvents.length > 0) {
        const event = pendingRefusalEvents.shift()!;
        try {
            listener(event);
        } catch {
            // Reporting must never block the recovery.
        }
    }
    return () => {
        refusalListeners.delete(listener);
    };
};

const handleDefinitiveRefusal = (error: unknown): void => {
    if (refusalHandled) return;
    refusalHandled = true;
    const inBrowser = typeof window !== 'undefined';
    const afterReload = inBrowser && readReloadMark() !== null;
    const willReload = inBrowser && reloadOnceOnRefusal && !afterReload;
    logger.warn('App Check refused this browser', {
        error: error instanceof Error ? error.message : String(error),
        afterReload,
        willReload,
    });
    setAppCheckHealth('refused');
    emitRefusalEvent({ kind: 'refused', afterReload, willReload });
    if (willReload) {
        try {
            window.sessionStorage.setItem(RELOAD_MARK_KEY, String(Date.now()));
        } catch {
            // No session storage (private mode): without a mark we cannot
            // guarantee a single reload, so do not reload at all.
            return;
        }
        window.location.reload();
    }
};

/** A successful mint clears any accumulated failure state and marks healthy. */
const recordAttestationSuccess = (): void => {
    consecutiveTokenFailures = 0;
    setAppCheckHealth('healthy');
    // The recovery reload worked: report it once and clear the mark, so a
    // refusal later in this session gets its own single reload.
    if (!recoveryReported && typeof window !== 'undefined' && readReloadMark() !== null) {
        recoveryReported = true;
        try {
            window.sessionStorage.removeItem(RELOAD_MARK_KEY);
        } catch {
            // Nothing to clear.
        }
        emitRefusalEvent({ kind: 'recovered-after-reload', afterReload: true, willReload: false });
    }
};

/**
 * Record a hard attestation failure. Only flips to `degraded` once failures
 * cross {@link DEGRADED_FAILURE_THRESHOLD} consecutively — below that the
 * health signal is left untouched (stays `unknown`/`healthy`) so transient
 * blips never show the banner.
 */
const recordAttestationFailure = (): void => {
    consecutiveTokenFailures += 1;
    if (consecutiveTokenFailures >= DEGRADED_FAILURE_THRESHOLD) {
        setAppCheckHealth('degraded');
    }
};

/**
 * Read the current App Check health signal.
 */
export const getAppCheckHealth = (): AppCheckHealth => appCheckHealth;

/**
 * Upper bound on how long we wait for a reCAPTCHA Enterprise token mint
 * before proceeding without attestation. The token is attached best-effort
 * (see {@link getAppCheckToken}) and the API runs App Check in monitor mode,
 * so a missing token never fails a request — but an unbounded `getToken()`
 * can stall the FIRST request on a fresh page load (e.g. the admin sign-in
 * that gates the whole app) if the reCAPTCHA challenge is slow. Bounding it
 * keeps login responsive while still attaching a token on the happy path.
 */
const APP_CHECK_TOKEN_TIMEOUT_MS = 1500;

/**
 * Subscribe to App Check health transitions. Returns an unsubscribe function.
 */
export const subscribeAppCheckHealth = (
    cb: (h: AppCheckHealth) => void
): (() => void) => {
    healthSubscribers.add(cb);
    return () => {
        healthSubscribers.delete(cb);
    };
};

/**
 * Initialize Firebase App Check for web applications.
 *
 * Uses reCAPTCHA Enterprise as the attestation provider for web clients.
 * This is the web equivalent of Play Integrity (Android) / App Attest (iOS).
 *
 * Environment variables:
 *   - NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY: reCAPTCHA Enterprise site key
 *     (required in production/staging, optional in development)
 *
 * Emulator mode:
 *   - When `useEmulators` is true, App Check is skipped because the Firebase
 *     emulator suite does not support App Check verification.
 *
 * Debug tokens:
 *   - In development (non-emulator), set `FIREBASE_APPCHECK_DEBUG_TOKEN=true`
 *     in the browser console or environment to use debug tokens. The debug
 *     token must be registered in the Firebase Console.
 *
 * @param app - The initialized Firebase app instance
 * @param options.useEmulators - Whether Firebase emulators are active
 */
export const initializeFirebaseAppCheck = (
    app: FirebaseApp,
    options: { useEmulators?: boolean } = {}
): AppCheck | null => {
    if (options.useEmulators) {
        logger.debug('App Check skipped — running with emulators');
        return null;
    }

    if (appCheckInstance) {
        logger.debug('App Check already initialized');
        return appCheckInstance;
    }

    const envVars = getEnvVars();
    const siteKey = envVars.RECAPTCHA_ENTERPRISE_SITE_KEY;

    if (!siteKey) {
        logger.warn(
            'App Check not initialized — RECAPTCHA_ENTERPRISE_SITE_KEY not set. ' +
            'Use NEXT_PUBLIC_RECAPTCHA_ENTERPRISE_SITE_KEY (Next.js) or VITE_RECAPTCHA_ENTERPRISE_SITE_KEY (Vite).'
        );
        return null;
    }

    try {
        // Debug tokens are ONLY for local dev servers (`pnpm dev`).
        // Both Next.js and Vite set MODE='production' for any built output,
        // including all Vercel deployments (dev/staging/prod preview URLs).
        // Local dev servers have MODE='development'.
        const isLocalDevServer = envVars.MODE === 'development';

        if (isLocalDevServer && typeof window !== 'undefined') {
            // @ts-expect-error — Firebase SDK reads this global to enable debug mode
            self.FIREBASE_APPCHECK_DEBUG_TOKEN = envVars.FIREBASE_APPCHECK_DEBUG_TOKEN || true;
        }

        appCheckInstance = initializeAppCheck(app, {
            provider: new ReCaptchaEnterpriseProvider(siteKey),
            isTokenAutoRefreshEnabled: true,
        });

        logger.info('App Check initialized with reCAPTCHA Enterprise');
        return appCheckInstance;
    } catch (error) {
        logger.error('Failed to initialize App Check', {
            error: error instanceof Error ? error.message : String(error),
        });
        // App Check could not initialize. Record it as an attestation failure
        // and route through the error handler so it reaches Sentry — but don't
        // flip straight to `degraded`: in monitor mode sign-in still works, so
        // a one-off init hiccup shouldn't show the "sign-in unavailable" banner.
        // Sustained token-mint failures (below) are what cross the threshold.
        recordAttestationFailure();
        errorHandler.handleError(
            error,
            { component: 'AppCheck', operation: 'initialize' },
            { logLevel: 'warn' }
        );
        return null;
    }
};

/**
 * Get a current App Check token for attaching to API requests.
 *
 * Returns null if App Check is not initialized (emulator mode, missing
 * site key, or initialization failure). Callers should treat this as
 * best-effort — the API server's enforcement mode determines whether
 * missing tokens cause request rejection.
 *
 * @param forceRefresh - Force a new token even if the cached one is valid
 */
export const getAppCheckToken = async (
    forceRefresh = false
): Promise<string | null> => {
    if (!appCheckInstance) {
        return null;
    }

    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    try {
        // Bound the mint so a slow reCAPTCHA Enterprise challenge can't stall
        // the request that triggered it. The token is best-effort (the API
        // runs App Check in monitor mode), so on timeout we return null — but
        // a timeout is "slow", NOT "broken", so it does not count toward the
        // degraded-banner threshold (see AppCheckTokenTimeoutError handling).
        const tokenResult = await Promise.race([
            getToken(appCheckInstance, forceRefresh),
            new Promise<never>((_, reject) => {
                timeoutTimer = setTimeout(
                    () =>
                        reject(
                            new AppCheckTokenTimeoutError(
                                `App Check token mint timed out after ${APP_CHECK_TOKEN_TIMEOUT_MS}ms`,
                            ),
                        ),
                    APP_CHECK_TOKEN_TIMEOUT_MS,
                );
            }),
        ]);
        recordAttestationSuccess();
        return tokenResult.token;
    } catch (error) {
        // A timeout means the mint was slow, not that attestation is down.
        // Return null (best-effort contract) WITHOUT counting it as a failure,
        // so a cold first-load challenge never trips the outage banner.
        if (error instanceof AppCheckTokenTimeoutError) {
            logger.debug('App Check token mint timed out (slow, not degraded)', {
                timeoutMs: APP_CHECK_TOKEN_TIMEOUT_MS,
            });
            return null;
        }

        // Refused: final for this page. Recorded once — not as one more
        // "failure" per ask, which is how a refused tab logged the same
        // block 190 times in eight seconds.
        if (isDefinitiveAppCheckRefusal(error)) {
            handleDefinitiveRefusal(error);
            return null;
        }

        logger.warn('Failed to get App Check token', {
            error: error instanceof Error ? error.message : String(error),
        });
        // A hard mint failure. Count it; the banner only appears once failures
        // are sustained (see DEGRADED_FAILURE_THRESHOLD). Surface to monitoring
        // either way, then keep the best-effort contract and return null.
        recordAttestationFailure();
        errorHandler.handleError(
            error,
            { component: 'AppCheck', operation: 'getToken' },
            { logLevel: 'warn' }
        );
        return null;
    } finally {
        if (timeoutTimer) clearTimeout(timeoutTimer);
    }
};

/**
 * Get the App Check instance (if initialized).
 * Useful for passing to Firebase SDK functions that accept AppCheck.
 */
/**
 * Resolves once App Check has a token — or once we have waited long enough that
 * blocking any longer would be worse than proceeding without one.
 *
 * WHY FIRESTORE NEEDS THIS AND THE API DOES NOT.
 *
 * `getAppCheckToken` is deliberately best-effort, and its own comment explains
 * why: "the API runs App Check in monitor mode". True for the API. NOT true for
 * Firestore, which is ENFORCED in production — every read must carry a valid
 * token or the server refuses it.
 *
 * The refusal is silent. A denied read does not throw: the SDK falls back to the
 * local cache, finds nothing, and reports the document as NOT EXISTING. So a
 * read that raced the token is indistinguishable from "this user has no
 * profile", and the app confidently renders "we couldn't load your profile" for
 * an account that is perfectly fine.
 *
 * That race is invisible on Chrome, where the reCAPTCHA Enterprise challenge
 * resolves in milliseconds, and reliably lost on Safari, where tracking
 * prevention makes it slow or impossible — which is why this presented as a
 * browser bug rather than an ordering bug (prod, 2026-08-10).
 *
 * NEVER REJECTS, AND ALWAYS SETTLES. A permanently broken attestation must
 * degrade to the old behaviour (reads attempted without a token) rather than
 * hang the app forever — the failure this replaces was itself a wait that
 * outlived its usefulness.
 *
 * TWO THINGS THIS GATE MUST NOT DO, both of which it originally did.
 *
 * 1. IT MUST WAIT FOR A TOKEN, NOT MERELY FOR AN OUTCOME. `getAppCheckToken`
 *    carries its OWN 1.5s bound and, on expiry, RESOLVES NULL rather than
 *    rejecting — it is best-effort by contract. Racing it against the ceiling
 *    and discarding its value (`.then(() => undefined)`) therefore settled the
 *    gate ~1.5s in with no token in hand, and the 8s ceiling was unreachable
 *    because the inner bound always won. "We gave up" was being read as
 *    "ready", so the gate let through exactly the token-less reads it exists
 *    to prevent. A cold challenge is SLOW, not broken; the answer is to ask
 *    again until the ceiling, not to accept the first miss.
 *
 * 2. IT MUST NOT LATCH A TOKEN-LESS OUTCOME. The promise is a module
 *    singleton, so a first attempt that ended empty was memoised for the life
 *    of the DOCUMENT: every later read returned instantly, still token-less,
 *    and no retry could ever reach attestation again. That is what made the
 *    profile page's "Try again" button structurally incapable of recovering —
 *    it re-ran the query against a gate that had already given up — and why
 *    the only cure was retyping the URL, which discards the module and starts
 *    a fresh singleton against a by-then-warm challenge (prod, 2026-08-11).
 *    A SUCCESS is latched (it costs nothing to reuse); a FAILURE re-arms.
 */
let appCheckReadyPromise: Promise<void> | null = null;

/** Set once a real token has been minted. Latches — success is permanent. */
let appCheckTokenReady = false;

/** Ceiling on the pre-read wait. Past this, proceeding beats blocking. */
const APP_CHECK_READY_MAX_MS = 8_000;

/** Pause between mint attempts, so a cold challenge is retried, not spun on. */
const APP_CHECK_READY_RETRY_MS = 250;

export const whenAppCheckReady = (): Promise<void> => {
    // No App Check configured (emulators, SSR, missing site key) — nothing to
    // wait for, and waiting would stall every read for no reason.
    if (!appCheckInstance) return Promise.resolve();

    // Already attested. Reads proceed with zero added latency from here on.
    if (appCheckTokenReady) return Promise.resolve();

    // Refused: no token is coming on this page, so waiting only delays the
    // error state the guest needs to see.
    if (appCheckHealth === 'refused') return Promise.resolve();

    if (!appCheckReadyPromise) {
        const attempt = (async (): Promise<void> => {
            const deadline = Date.now() + APP_CHECK_READY_MAX_MS;
            for (;;) {
                // Never rejects by contract, but a caught throw would be
                // indistinguishable from a miss anyway — treat both as "retry".
                const token = await getAppCheckToken().catch(() => null);
                if (token) {
                    appCheckTokenReady = true;
                    return;
                }
                // Re-read: a refusal lands DURING the ask above.
                if (getAppCheckHealth() === 'refused') return;
                // Only sleep if a further attempt could still finish in time;
                // otherwise stop now rather than overshoot the ceiling.
                if (Date.now() + APP_CHECK_READY_RETRY_MS >= deadline) return;
                await new Promise<void>((resolve) =>
                    setTimeout(resolve, APP_CHECK_READY_RETRY_MS),
                );
            }
        })();

        appCheckReadyPromise = attempt.finally(() => {
            // Re-arm on failure ONLY. Concurrent callers already hold this
            // promise and still settle; the next caller starts a fresh window,
            // which is what makes a retry — user-driven or poll-driven —
            // able to reach attestation again.
            if (!appCheckTokenReady) appCheckReadyPromise = null;
        });
    }
    return appCheckReadyPromise;
};

export const getFirebaseAppCheck = (): AppCheck | null => appCheckInstance;
